/**
 * 标准配合定义模块。
 *
 * 文件职责：
 * 1. 定义装配体标准配合的可序列化主状态；
 * 2. 定义配合实体引用与求解状态协议；
 * 3. 保证文档、预览、命令和 UI 共享同一份领域语言。
 */

import type { ComponentTransformSnapshot } from '../component-instance';
import type { AssemblyReferenceDependencySnapshot } from './AssemblyReferenceDependency';

/** 装配体配合定义协议版本。 */
export const ASSEMBLY_MATE_SCHEMA_VERSION = 1;

/**
 * 装配体标准配合类型。
 */
export enum AssemblyMateType {
  /** 重合配合。 */
  Coincident = 'coincident',
  /** 平行配合。 */
  Parallel = 'parallel',
  /** 垂直配合。 */
  Perpendicular = 'perpendicular',
  /** 相切配合。 */
  Tangent = 'tangent',
  /** 同轴心配合。 */
  Concentric = 'concentric',
  /** 锁定配合。 */
  Lock = 'lock',
  /** 距离配合。 */
  Distance = 'distance',
  /** 角度配合。 */
  Angle = 'angle',
}

/**
 * 配合实体语义类型。
 */
export enum MateEntityKind {
  /** 平面面或基准面。 */
  Plane = 'plane',
  /** 圆柱面。 */
  Cylinder = 'cylinder',
  /** 圆锥面。 */
  Cone = 'cone',
  /** 球面。 */
  Sphere = 'sphere',
  /** 暂不可解析的曲面。 */
  Surface = 'surface',
  /** 直线边或直线基准。 */
  Line = 'line',
  /** 圆形或圆弧边。 */
  CircularEdge = 'circularEdge',
  /** 暂不可解析的曲线。 */
  Curve = 'curve',
  /** 顶点、基准点或原点。 */
  Point = 'point',
  /** 原点。 */
  Origin = 'origin',
  /** 坐标系。 */
  CoordinateSystem = 'coordinateSystem',
  /** 基准轴。 */
  ReferenceAxis = 'referenceAxis',
  /** 基准面。 */
  ReferencePlane = 'referencePlane',
  /** 不支持实体。 */
  Unsupported = 'unsupported',
}

/**
 * 配合对齐方向。
 */
export enum MateAlignment {
  /** 法向或轴向同向。 */
  Aligned = 'aligned',
  /** 法向或轴向反向。 */
  AntiAligned = 'anti-aligned',
}

/**
 * 距离配合的尺寸测量条件。
 */
export enum MateDistanceMode {
  /** 使用圆柱、圆边或球等解析实体的中心线/中心点距离。 */
  CenterToCenter = 'centerToCenter',
  /** 使用两个实体之间最近处的距离。 */
  Minimum = 'minimum',
  /** 使用两个实体之间最远处的距离。 */
  Maximum = 'maximum',
}

/**
 * 距离配合中每个圆形实体的测量条件。
 *
 * SOLIDWORKS 对圆柱面、圆边等圆形实体不是只有一个全局距离条件，而是允许
 * 第一圆形实体和第二圆形实体分别选择中心、最小或最大。该结构只保存纯数据，
 * 不依赖运行时对象，用于求解、测量、持久化和 UI 展示保持同一语义。
 */
export interface MateDistanceEntityModes {
  /** 第一选择实体的圆形距离条件。 */
  readonly firstEntityMode: MateDistanceMode;
  /** 第二选择实体的圆形距离条件。 */
  readonly secondEntityMode: MateDistanceMode;
}

/**
 * 同轴心错位配合的偏差分配方式。
 */
export enum MateConcentricMisalignmentType {
  /**
   * 当前 Mate 精确同轴，偏差全部分配给链接的同轴心 Mate。
   */
  AlignThisMate = 'alignThisMate',
  /**
   * 链接的同轴心 Mate 精确同轴，偏差全部保留在当前 Mate。
   */
  AlignLinkedMate = 'alignLinkedMate',
  /**
   * 当前 Mate 与链接 Mate 平均分摊偏差。
   */
  Symmetric = 'symmetric',
}

/**
 * 配合求解状态。
 */
export enum MateSolveStatus {
  /** 尚未求解。 */
  NotRun = 'notRun',
  /** 求解成功。 */
  Solved = 'solved',
  /** 无法在当前输入下求解。 */
  Unsolved = 'unsolved',
  /** 约束过定义。 */
  OverDefined = 'overDefined',
  /** 持久引用缺失或无法恢复。 */
  MissingReference = 'missingReference',
  /** 几何类型暂不支持。 */
  UnsupportedGeometry = 'unsupportedGeometry',
}

/**
 * 配合数值维度。
 */
export interface MateDimension {
  /** 维度类型。 */
  readonly kind: AssemblyMateType.Distance | AssemblyMateType.Angle;
  /** 数值；距离使用模型单位，角度使用弧度。 */
  readonly value: number;
  /** 是否翻转维度方向。 */
  readonly flipped: boolean;
  /** 距离配合的测量条件；角度配合不使用。 */
  readonly distanceMode?: MateDistanceMode;
  /**
   * 距离配合的逐实体圆形测量条件。
   *
   * 该字段用于 SOLIDWORKS 的第一/第二圆弧条件。旧数据只包含 `distanceMode` 时，
   * 求解器会按旧全局模式推导两个实体的条件，保持历史文档可恢复。
   */
  readonly distanceEntityModes?: MateDistanceEntityModes;
}

/**
 * 锁定配合的相对位姿快照。
 */
export interface MateLockedRelativeTransform {
  /** 第一组件到第二组件的相对位姿。 */
  readonly transform: ComponentTransformSnapshot;
}

/**
 * 同轴心错位配合参数。
 */
export interface MateConcentricMisalignment {
  /** 偏差分配方式。 */
  readonly type: MateConcentricMisalignmentType;
  /**
   * 与当前 Mate 组成错位同轴心组的另一条 Mate ID。
   *
   * 设计说明：
   * SOLIDWORKS 的 misaligned concentric mates 是一对 concentric mates 的关系，
   * 不能只靠当前实体独立表达。因此持久化时必须保存 linkedMateId，重载后才能恢复
   * 哪一条 Mate 精确求解、哪一条 Mate 只校验最大偏差。
   */
  readonly linkedMateId: string;
  /** 允许的最大错位距离，使用模型 world/render space 单位。 */
  readonly maximumDeviation: number;
}

/**
 * 配合实体引用。
 *
 * 设计说明：
 * 1. `stableRef` 是装配实例级引用，不依赖运行期 Object3D.uuid；
 * 2. `geometry` 是求解器当前使用的世界空间几何快照；
 * 3. 保存时以 `topologyRef` 恢复引用，以 `geometry` 提供重载失败前的诊断上下文。
 */
export interface MateEntityRef {
  /** 引用协议版本。 */
  readonly schemaVersion: typeof ASSEMBLY_MATE_SCHEMA_VERSION;
  /** 实体稳定 ID。 */
  readonly stableRef: string;
  /** 所属装配节点 ID。 */
  readonly assemblyNodeId: string;
  /** 所属组件实例 ID。 */
  readonly componentInstanceId: string;
  /** 来源零件或子装配 UUID。 */
  readonly partUuid?: string;
  /** 实体语义类型。 */
  readonly entityKind: MateEntityKind;
  /** 装配拓扑稳定引用。 */
  readonly topologyRef?: AssemblyTopologySelectionRef;
  /**
   * 装配体基准实体稳定引用。
   *
   * 设计说明：
   * 基准点/轴/面不属于 imported BRep 拓扑，必须用独立引用恢复，避免把它们伪装成面/边/点拓扑引用。
   */
  readonly referenceRef?: AssemblyReferenceSelectionRef;
  /**
   * 派生基准与其创建参考对象之间的稳定依赖。
   *
   * 设计说明：
   * 默认基准面、基准轴和原点不携带该字段，仍按固定虚拟组件求解；
   * 由参考对象创建的基准会通过该字段把 Mate 移动目标转交给驱动组件。
   */
  readonly referenceDependency?: AssemblyReferenceDependencySnapshot;
  /**
   * 选择该实体时组件的位姿。
   *
   * 设计说明：
   * 该字段保存组件相对父节点的 local 位姿，主要用于历史、锁定配合相对姿态和顶层装配兜底。
   * Mate Definition 内的几何描述符按选择瞬间的 world space 保存；当父级装配节点存在 transform 时，
   * 求解器必须优先使用 `componentWorldTransformAtSelection` 计算 world delta，避免把 local 位姿误当 world。
   */
  readonly componentTransformAtSelection?: ComponentTransformSnapshot;
  /**
   * 选择该实体时组件根的 world 位姿。
   *
   * 设计说明：
   * imported BRep 描述符统一以 world space 输出，子装配父节点存在位姿时，local transform 无法表达
   * 真实 world 基线。该字段只保存可序列化矩阵分解结果，不包含 Object3D 引用，用于求解器和命令态高亮
   * 在预览、拖动和重载恢复后把实体几何稳定还原到当前 world space。
   */
  readonly componentWorldTransformAtSelection?: ComponentTransformSnapshot;
  /** 求解器当前消费的 BRep 几何描述符。 */
  readonly geometry: BRepGeometryDescriptor;
  /** UI 展示名称。 */
  readonly label: string;
}

/**
 * 标准配合定义。
 */
export interface AssemblyMateDefinition {
  /** 定义协议版本。 */
  readonly schemaVersion: typeof ASSEMBLY_MATE_SCHEMA_VERSION;
  /** 配合稳定 ID。 */
  readonly mateId: string;
  /** 所属装配体 ID。 */
  readonly assemblyId: string;
  /** 标准配合类型。 */
  readonly mateType: AssemblyMateType;
  /** 第一选择实体。 */
  readonly firstEntity: MateEntityRef;
  /** 第二选择实体。 */
  readonly secondEntity: MateEntityRef;
  /**
   * 角度配合参考实体。
   *
   * SOLIDWORKS 通过参考实体定义角度配合的旋转轴，从而避免拖动、重载或切换配置后出现镜像翻转。
   * 该字段只保存稳定拓扑引用与几何描述符，不包含运行时 Object3D。
   */
  readonly referenceEntity?: MateEntityRef;
  /** 对齐方向。 */
  readonly alignment: MateAlignment;
  /** 距离/角度维度。 */
  readonly dimension?: MateDimension;
  /** 同心配合是否锁定旋转。 */
  readonly lockRotation?: boolean;
  /** 同轴心错位配合参数。 */
  readonly concentricMisalignment?: MateConcentricMisalignment;
  /** 锁定配合的相对位姿。 */
  readonly lockedRelativeTransform?: MateLockedRelativeTransform;
  /** 是否被抑制。 */
  readonly suppressed: boolean;
  /** 当前求解状态。 */
  readonly solveStatus: MateSolveStatus;
  /** 当前错误信息。 */
  readonly errorMessage?: string;
  /** 创建时间戳。 */
  readonly createdAt: number;
  /** 更新时间戳。 */
  readonly updatedAt: number;
}

/**
 * 创建配合 ID。
 *
 * @returns 配合稳定 ID。
 */
export function createAssemblyMateId(): string {
  return `assembly-mate:${Date.now()}:${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * 克隆组件 transform 快照。
 *
 * @param transform 源 transform。
 * @returns 可安全持久化的 transform 副本。
 */
export function cloneComponentTransformSnapshot(
  transform: ComponentTransformSnapshot,
): ComponentTransformSnapshot {
  return {
    position: [
      transform.position[0],
      transform.position[1],
      transform.position[2],
    ],
    quaternion: [
      transform.quaternion[0],
      transform.quaternion[1],
      transform.quaternion[2],
      transform.quaternion[3],
    ],
    scale: [
      transform.scale[0],
      transform.scale[1],
      transform.scale[2],
    ],
  };
}
